import {
  Box,
  Button,
  Container,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Portal,
  SimpleGrid,
  Spinner,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "wouter";

import { useExcelImportsList } from "@nens/ldo/api-v1";
import { Breadcrumbs } from "@nens/ldo/components";
import { useExcelImportPageHook } from "@nens/ldo/hooks";
import { OnBeforeUnload } from "@nens/react/components";
import { useLocalStorage } from "@nens/react/hooks";

import { DownloadSheet } from "./import-steps/download-sheet";
import { ExcelImportRow } from "./import-steps/excel-import-row";
import { SelectFiles } from "./import-steps/select-files";
import { ValidateFiles } from "./import-steps/validate-files";

interface ImportPageProps {
  page: string;
}

export default function ImportPage({ page }: ImportPageProps) {
  const intl = useIntl();
  const setLocation = useLocation()[1];

  const excelImports = useExcelImportsList();
  const {
    mode,
    setMode,
    activeStep,
    readyForStep,
    addFiles,
    removeFile,
    processFiles,
    files,
    startOver,
    excelImport,
    validationErrors,
    STEP_CHOOSE_MODE,
    STEP_UPLOAD,
    STEP_VALIDATION,
    STEP_IMPORT,
    STEP_SUCCESS,
  } = useExcelImportPageHook();

  // Use local storage because people are going to F5 this
  const [openExcelImport, setOpenExcelImport] = useLocalStorage("ldo_open_excel_import", -1);

  if (page !== "new" && page !== "recent") {
    setLocation("/import/new", { replace: true });
    return null;
  }

  const index = page === "new" ? 0 : 1;
  const setIndex = (index: number) => {
    setLocation(index === 0 ? "/import/new" : "/import/recent");
  };

  return (
    <Box w="100%">
      <Breadcrumbs
        crumbs={[
          { label: intl.formatMessage({ id: "pages.home", defaultMessage: "Home" }), href: "/" },
          {
            label: intl.formatMessage({ id: "pages.import", defaultMessage: "Import" }),
            href: "/import",
          },
        ]}
      />
      <Container maxW={"7xl"} py={"24px"} justifyItems="center">
        <Tabs index={index} onChange={setIndex}>
          <TabList>
            <Tab>
              <FormattedMessage id="pages.import.tabs.new_import" defaultMessage="New Import" />
            </Tab>
            <Tab>
              <FormattedMessage
                id="pages.import.tabs.recent_imports"
                defaultMessage="Recent Imports"
              />
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <Stepper size="lg" index={activeStep} orientation="vertical" m={4}>
                <Step>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>
                  <Box w="100%">
                    <StepTitle>
                      <FormattedMessage id="steps.download.title" defaultMessage="Download" />
                    </StepTitle>
                    <StepDescription>
                      <FormattedMessage
                        id="steps.download.description"
                        defaultMessage="Download empty Excel file"
                      />
                    </StepDescription>
                    <Box m={4}>
                      <DownloadSheet
                        setMode={setMode}
                        mode={mode}
                        done={activeStep > STEP_CHOOSE_MODE}
                      />
                    </Box>
                  </Box>
                  <StepSeparator />
                </Step>

                <Step>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>
                  <Box w="100%">
                    <StepTitle>
                      <FormattedMessage id="steps.upload.title" defaultMessage="Upload" />
                    </StepTitle>
                    <StepDescription>
                      {mode === "create" ? (
                        <FormattedMessage
                          id="steps.upload_both.description"
                          defaultMessage="Select one Excel (.xlsx) and one ZIP (.zip) file to import"
                        />
                      ) : mode === "update" ? (
                        <FormattedMessage
                          id="steps.upload_excel.description"
                          defaultMessage="Select one Excel (.xlsx) file to import"
                        />
                      ) : null}
                    </StepDescription>
                    <Box m={4}>
                      <SelectFiles
                        mode={mode}
                        active={activeStep === STEP_UPLOAD}
                        done={activeStep > STEP_UPLOAD}
                        files={files}
                        filesValid={readyForStep(STEP_VALIDATION)}
                        nextStep={processFiles}
                        addFiles={addFiles}
                        removeFile={removeFile}
                      />
                    </Box>
                  </Box>
                  <StepSeparator />
                </Step>

                <Step>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>
                  <Box w="100%">
                    <StepTitle>
                      <FormattedMessage id="steps.validation.title" defaultMessage="Validation" />
                    </StepTitle>
                    <StepDescription>
                      <FormattedMessage
                        id="steps.validation.description"
                        defaultMessage="Validate the contents of the uploaded files"
                      />
                    </StepDescription>
                    <Box m={4}>
                      <ValidateFiles
                        active={activeStep === STEP_VALIDATION}
                        validationErrors={validationErrors}
                        startOver={startOver}
                      />
                    </Box>
                  </Box>
                  <StepSeparator />
                </Step>

                <Step>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>
                  <Box w="100%">
                    <StepTitle>
                      <FormattedMessage id="steps.import.title" defaultMessage="Import" />
                    </StepTitle>
                    <StepDescription>
                      <FormattedMessage
                        id="steps.import.description"
                        defaultMessage="Actually import the scenarios"
                      />
                    </StepDescription>
                    <Box m={4}>
                      {/* See the modal at the bottom that is also shown */}
                      <OnBeforeUnload active={activeStep === STEP_IMPORT} />
                      {activeStep === STEP_IMPORT ? <Spinner /> : null}
                    </Box>
                  </Box>
                  <StepSeparator />
                </Step>

                <Step>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>
                  <Box w="100%">
                    <StepTitle>
                      <FormattedMessage id="steps.success.title" defaultMessage="Success" />
                    </StepTitle>
                    <StepDescription>
                      <FormattedMessage
                        id="steps.success.description"
                        defaultMessage="Import successfully finished"
                      />
                    </StepDescription>
                    <Box m={4}>
                      {activeStep === STEP_SUCCESS ? (
                        <>
                          <FormattedMessage
                            id="steps.success.message"
                            defaultMessage="Success! {num_scenarios} scenarios imported."
                            values={{ num_scenarios: excelImport?.scenario_ids.length }}
                          />
                          <Button m={4} onClick={startOver}>
                            New import
                          </Button>
                        </>
                      ) : null}
                    </Box>
                  </Box>
                  <StepSeparator />
                </Step>
              </Stepper>
            </TabPanel>
            <TabPanel>
              <SimpleGrid columns={1} spacing={0} border="1px solid #ddd">
                <Flex
                  p="4"
                  justifyContent="flex-start"
                  cursor="pointer"
                  _hover={{ bgColor: "#FCF5F450" }}
                >
                  <Box w="160px">
                    <FormattedMessage id="tableheader.created_at" defaultMessage="Created at" />
                  </Box>
                  <Box w="70px">
                    <FormattedMessage id="tableheader.status" defaultMessage="Status" />
                  </Box>
                  <Box w="250px">
                    <FormattedMessage id="tableheader.error" defaultMessage="Error" />
                  </Box>
                  <Box w="70px">
                    <FormattedMessage id="tableheader.type" defaultMessage="Type" />
                  </Box>
                  <Box w="70px">
                    <FormattedMessage id="tableheader.scenarios" defaultMessage="Scenarios" />
                  </Box>
                  <Box w="290px">
                    <FormattedMessage id="tableheader.user" defaultMessage="User" />
                  </Box>
                </Flex>
                {excelImports.data?.items.map((item, idx) => (
                  <ExcelImportRow
                    key={idx}
                    excelImport={item}
                    open={openExcelImport === item.id}
                    toggleOpen={() =>
                      setOpenExcelImport(item.id === openExcelImport ? -1 : item.id)
                    }
                  />
                ))}
              </SimpleGrid>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
      <Portal>
        <Modal isOpen={activeStep === STEP_IMPORT} isCentered onClose={() => undefined}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <FormattedMessage id="modal.uploading" defaultMessage="Uploading" />
            </ModalHeader>
            <ModalBody>
              <Text>
                <FormattedMessage
                  id="modal.uploading.body"
                  defaultMessage="Uploading files. Please stay on the page..."
                />
              </Text>
              <Spinner />
            </ModalBody>
          </ModalContent>
        </Modal>
      </Portal>
    </Box>
  );
}
